import { useState, useCallback, useMemo } from 'react';
import {
  allHotspots,
  getHotspotById,
  stageInfo,
} from '../data/notationHotspots';
import type { Hotspot, Stage } from '../data/notationHotspots';
import { ExperienceScene } from '../components/experience/ExperienceScene';
import { NotationPanel } from '../components/experience/NotationPanel';
import { ProgressBadge } from '../components/experience/ProgressBadge';
import { GuidedMode } from '../components/experience/GuidedMode';
import { StageProgressBar } from '../components/experience/StageProgressBar';
import { StageOrientation } from '../components/experience/StageOrientation';
import { OverlayWords } from '../components/experience/OverlayWords';
import { CapstoneView } from '../components/experience/CapstoneView';
import { MobileStageDrawer } from '../components/experience/MobileStageDrawer';
import { AdUnit } from '../components/AdUnit';
import './ExperienceNotation.css';

export function ExperienceNotation() {
  const [currentStage, setCurrentStage] = useState<Stage>(0);
  const [selectedHotspot, setSelectedHotspot] = useState<Hotspot | null>(null);
  const [exploredIds, setExploredIds] = useState<Set<string>>(new Set());
  const [showOverlay, setShowOverlay] = useState(false);
  const [completionReached, setCompletionReached] = useState(false);

  // Track explored IDs per stage
  const exploredByStage = useMemo(() => {
    const result: Record<Stage, Set<string>> = { 0: new Set(), 1: new Set(), 2: new Set(), 3: new Set(), 4: new Set() };
    exploredIds.forEach(id => {
      const hotspot = getHotspotById(id);
      if (hotspot) {
        result[hotspot.stage].add(id);
      }
    });
    return result;
  }, [exploredIds]);

  const handleSelectHotspot = useCallback((id: string) => {
    const hotspot = getHotspotById(id);
    if (hotspot) {
      // Auto-switch to the hotspot's stage
      if (hotspot.stage !== currentStage) {
        setCurrentStage(hotspot.stage);
      }
      setSelectedHotspot(hotspot);
      setExploredIds(prev => new Set([...prev, id]));
      setShowOverlay(true);
      setTimeout(() => setShowOverlay(false), 2500);
    }
  }, [currentStage]);

  // Panel-only selection for Stage 4 — opens side panel without switching stage
  const handleCapstoneSelect = useCallback((id: string) => {
    const hotspot = getHotspotById(id);
    if (hotspot) {
      setSelectedHotspot(hotspot);
      setExploredIds(prev => new Set([...prev, id]));
    }
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedHotspot(null);
    setShowOverlay(false);
  }, []);

  const handleBegin = useCallback(() => {
    setCurrentStage(1);
  }, []);

  const handleAdvanceStage = useCallback((stage: Stage) => {
    setCurrentStage(stage);
    setSelectedHotspot(null);
    if (stage === 4) {
      setCompletionReached(true);
    }
  }, []);

  const handleReset = useCallback(() => {
    setSelectedHotspot(null);
    setExploredIds(new Set());
    setCurrentStage(0);
    setShowOverlay(false);
    setCompletionReached(false);
  }, []);

  const handleReplayWithAwareness = useCallback(() => {
    setSelectedHotspot(null);
    setExploredIds(new Set());
    setCurrentStage(1);
    setShowOverlay(false);
    setCompletionReached(false);
  }, []);

  // Visible hotspots based on current stage
  const visibleHotspots = useMemo(() => {
    return allHotspots.filter(h => h.stage <= currentStage);
  }, [currentStage]);

  const totalVisible = visibleHotspots.length;

  // Stage 0: Orientation overlay
  if (currentStage === 0) {
    return (
      <div className="experience-notation">
        <StageOrientation onBegin={handleBegin} />
      </div>
    );
  }

  return (
    <div className={`experience-notation ${completionReached ? 'experience-notation--completed' : ''}`}>
      {/* Header */}
      <header className="experience-notation__header">
        <div className="experience-notation__title">
          <h1>Experience Notation</h1>
          <span className="experience-notation__stage-name" style={{ color: stageInfo[currentStage].color }}>
            {stageInfo[currentStage].name}
          </span>
        </div>
        <div className="experience-notation__pipeline-hint">
          {([1, 2, 3, 4] as Stage[]).map((stage, i) => (
            <span key={stage}>
              {i > 0 && <i className="fas fa-arrow-right pipeline-arrow"></i>}
              <span
                className={`pipeline-stage ${currentStage >= stage ? 'pipeline-stage--active' : ''}`}
                style={{ color: currentStage >= stage ? stageInfo[stage].color : undefined }}
              >
                {stageInfo[stage].label}
              </span>
            </span>
          ))}
        </div>
        <div className="experience-notation__controls">
          <ProgressBadge explored={exploredIds.size} total={totalVisible} />
          <button className="experience-notation__reset" onClick={handleReset}>
            <i className="fas fa-rotate-right"></i> Reset
          </button>
        </div>
      </header>

      {/* Main content area */}
      <div className={`experience-notation__main ${selectedHotspot ? 'experience-notation__main--panel-open' : ''}`}>
        {/* Left sidebar */}
        <aside className="experience-notation__sidebar">
          <StageProgressBar
            currentStage={currentStage}
            exploredByStage={exploredByStage}
            onSelectStage={handleAdvanceStage}
          />

          {currentStage >= 1 && currentStage <= 3 && (
            <GuidedMode
              currentStage={currentStage}
              onSelectHotspot={handleSelectHotspot}
              exploredIds={exploredIds}
            />
          )}

          {/* Square ad — pinned to sidebar bottom, desktop only */}
          <div className="experience-notation__sidebar-ad">
            <AdUnit slot="6723957185" format="auto" />
          </div>
        </aside>

        {/* Center - Scene or Capstone */}
        <div className="experience-notation__canvas">
          {currentStage === 4 ? (
            <CapstoneView
              selectedHotspot={selectedHotspot}
              exploredIds={exploredIds}
              onSelectHotspot={handleCapstoneSelect}
              onReset={handleReset}
              onReplay={handleReplayWithAwareness}
            />
          ) : (
            <>
              <ExperienceScene
                activeHotspot={selectedHotspot}
                exploredIds={exploredIds}
                currentStage={currentStage}
                onSelectHotspot={handleSelectHotspot}
              />
              <OverlayWords
                words={selectedHotspot?.overlayWords || []}
                isActive={showOverlay}
              />
            </>
          )}
        </div>

        {/* Notation panel */}
        <NotationPanel
          hotspot={selectedHotspot}
          onClose={handleClosePanel}
          onSelectRelated={currentStage === 4 ? handleCapstoneSelect : handleSelectHotspot}
          allHotspots={allHotspots}
          currentStage={currentStage}
        />

        {/* Mobile stage drawer (visible ≤1024px via CSS) */}
        <MobileStageDrawer
          currentStage={currentStage}
          exploredByStage={exploredByStage}
          exploredIds={exploredIds}
          onSelectStage={handleAdvanceStage}
          onSelectHotspot={handleSelectHotspot}
        />
      </div>
    </div>
  );
}
